/**
 * ¿A QUÉ CUENTA CONECTADA PERTENECE UN CHAT? — la regla, en un solo sitio.
 *
 * ⚠️ DOS COPIAS BYTE-IDÉNTICAS: `functions/utils/cuentaDelChat.cjs` y `src/utils/cuentaDelChat.js`
 * (este texto va en las dos). `src/` no puede importar de
 * `functions/` y las dos mitades necesitan la misma respuesta: el backend para decidir POR QUÉ
 * CUENTA sale cada mensaje (inbox, plantillas, seguimiento, recordatorios, bloqueo, acuses), la UI
 * para el selector de cuenta y la etiqueta de la tarjeta. Lo sostiene
 * `functions/scripts/check-shared-module-parity.js`. Diseño: MULTICUENTA-DISENO.md §2.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LAS DOS FORMAS QUE EXISTEN HOY (medidas el 21-oct-2026, 659 chats)
 * ───────────────────────────────────────────────────────────────────────────────
 *   · Instagram, Messenger y TikTok: `chat.accountId = {canal}_{externalId}`, que es el doc id de
 *     `channelAccounts` (198/198 chats).
 *   · WhatsApp Cloud API: `chat.numeroId = wa_api_{phone_number_id}` y `accountId: null`
 *     (460/461 chats; el que falta es un chat de abr-2026 sin ninguno de los dos).
 *
 * `accountId` es el campo común hacia adelante —los chats nuevos de WhatsApp lo llevan como
 * `whatsapp_{pnid}`—, y `numeroId` se mantiene por compatibilidad. Esta función lee los dos y
 * devuelve SIEMPRE la forma de `accountId`, para que ningún llamante vuelva a componerla.
 *
 * REGLAS, Y POR QUÉ:
 *   1. `accountId` manda si existe: es el dato explícito.
 *   2. Si no, `numeroId` sólo cuenta en WhatsApp y sólo con la forma Cloud (`wa_api_` + dígitos):
 *      un `numeroId` de Baileys (el E.164 pelado, `56966943462`) no es una cuenta de la Cloud API
 *      y no tiene `channelAccount`; devolverlo como cuenta inventaría una que no existe.
 *   3. Sin ninguno de los dos → `null`, NUNCA una cuenta por defecto de la org: el llamante
 *      decide (no enviar, mostrar «sin cuenta»). Es la regla de §4 del diseño.
 *   4. `canal` ausente se trata como WhatsApp, igual que el filtro del inbox
 *      (`Chats.js`: `(c.canal || 'whatsapp')`): misma lectura en las dos mitades.
 */

const CUENTA_CLOUD_RE = /^wa_api_(\d+)$/;

/**
 * @param {object|null|undefined} chat  el documento del chat (`canal`, `accountId`, `numeroId`)
 * @returns {string|null} el `accountId` (`{canal}_{externalId}`) de la cuenta, o null
 */
function cuentaDelChat(chat) {
  if (!chat || typeof chat !== 'object') return null;
  if (typeof chat.accountId === 'string' && chat.accountId.trim()) return chat.accountId.trim();
  const canal = chat.canal || 'whatsapp';
  if (canal !== 'whatsapp') return null;
  return cuentaDesdeNumeroId(chat.numeroId);
}

/**
 * La cuenta de WhatsApp de un `phone_number_id`: `whatsapp_{pnid}`. UNA composición (el router, el
 * handler de `user_id_update` y el lector de la marca la necesitan; tres copias a mano son tres
 * sitios donde divergir). Sólo dígitos; otra cosa → null.
 * @param {*} pnid
 * @returns {string|null}
 */
function cuentaDePnid(pnid) {
  const limpio = typeof pnid === 'string' ? pnid.trim() : (typeof pnid === 'number' ? String(pnid) : '');
  return /^\d+$/.test(limpio) ? `whatsapp_${limpio}` : null;
}

/**
 * La inversa para WhatsApp: `whatsapp_{pnid}` → `wa_api_{pnid}` (el doc id de `numeros`, lo que
 * entienden el sender y el secreto). Otra cosa → null.
 * @param {*} accountId
 * @returns {string|null}
 */
function numeroIdDeCuenta(accountId) {
  const m = typeof accountId === 'string' ? /^whatsapp_(\d+)$/.exec(accountId.trim()) : null;
  return m ? `wa_api_${m[1]}` : null;
}

/**
 * `wa_api_{pnid}` → `whatsapp_{pnid}`; cualquier otra forma → null.
 * @param {*} numeroId
 * @returns {string|null}
 */
function cuentaDesdeNumeroId(numeroId) {
  const m = typeof numeroId === 'string' ? CUENTA_CLOUD_RE.exec(numeroId.trim()) : null;
  return m ? cuentaDePnid(m[1]) : null;
}

/**
 * El nombre visible de una cuenta, desde su doc de `channelAccounts` (campos medidos el 21-oct).
 * Devuelve `{ principal, secundaria }` con cadenas, o null si no hay doc: el texto de «cuenta
 * desconectada» es de la UI (i18n), no de aquí.
 *
 * @param {object|null|undefined} channelAccount
 * @returns {{principal: string, secundaria: string|null}|null}
 */
function etiquetaDeCuenta(channelAccount) {
  if (!channelAccount || typeof channelAccount !== 'object') return null;
  const ca = channelAccount;
  const texto = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  switch (ca.canal) {
    case 'whatsapp': {
      const principal = texto(ca.display_phone_number) || texto(ca.numeroE164) || texto(ca.phone_number_id);
      return principal ? { principal, secundaria: texto(ca.verified_name) } : null;
    }
    case 'instagram': {
      const usuario = texto(ca.instagramUsername);
      return usuario ? { principal: `@${usuario.replace(/^@/, '')}`, secundaria: null } : null;
    }
    case 'messenger': {
      const pagina = texto(ca.pageName);
      return pagina ? { principal: pagina, secundaria: null } : null;
    }
    case 'tiktok': {
      const nombre = texto(ca.accountName) || texto(ca.username);
      return nombre ? { principal: nombre, secundaria: null } : null;
    }
    default:
      return null;
  }
}

module.exports = { cuentaDelChat, cuentaDesdeNumeroId, cuentaDePnid, numeroIdDeCuenta, etiquetaDeCuenta };
